// @docs:
//   - docs/wip/CREW_PAPER_REQUESTS.md  § API contract (skipper)
// ==============================================
// The skipper pages live inside the dashboard, so they use its slate surfaces
// and Ship's Papers' amber actions rather than the crew flow's navy — the
// Skipper-Request artboard's layout, copy and hierarchy, in the portal's
// colours. 44 px targets; nothing under 13 px (readability.test.ts).
// ==============================================

import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import type { RequestStatus } from '../types';

export const S = {
  card: 'bg-slate-800/60 border border-slate-700 rounded-2xl',
  label: 'text-[13px] font-semibold uppercase tracking-[0.12em] text-slate-400',
  note: 'text-sm leading-relaxed text-slate-400',
  field: 'flex flex-col gap-1.5 text-sm font-semibold text-slate-200',
  input: 'w-full min-h-[44px] rounded-lg bg-slate-900 border border-slate-600 text-white text-base px-3 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-400/70 aria-[invalid=true]:border-red-400',
  primary: 'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-amber-400 px-5 text-base font-bold text-slate-900 hover:brightness-105 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-200',
  ghost: 'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-slate-600 px-4 text-[15px] font-semibold text-white hover:border-amber-400 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/70',
  danger: 'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-red-400/60 px-4 text-[15px] font-semibold text-red-300 hover:bg-red-900/30 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300',
  link: 'font-semibold text-amber-300 hover:text-amber-200 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/70 rounded',
  err: 'text-sm text-red-300',
};

export function Section({ num, title, children, id }: { num: number; title: string; children: ReactNode; id?: string }) {
  return (
    <section className={`${S.card} flex flex-col gap-4 p-5 sm:p-6`} aria-labelledby={id ?? `sec-${num}`}>
      <h2 id={id ?? `sec-${num}`} className="m-0 flex items-center gap-3 text-lg font-bold text-white">
        <span className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-slate-700 font-mono text-[15px] text-amber-300" aria-hidden="true">{num}</span>
        {title}
      </h2>
      {children}
    </section>
  );
}

/** A row of mutually exclusive choices (aria-pressed buttons). */
export function Segmented<T extends string | number>({ label, value, options, onChange, id }: { label: string; value: T; options: Array<{ value: T; label: string }>; onChange: (v: T) => void; id?: string }) {
  return (
    <div role="group" aria-label={label} id={id} className="flex flex-wrap gap-1 rounded-xl border border-slate-700 bg-slate-900 p-1">
      {options.map((o) => (
        <button key={String(o.value)} type="button" aria-pressed={value === o.value} onClick={() => onChange(o.value)}
          className={`min-h-[40px] flex-1 whitespace-nowrap rounded-lg px-3 text-[15px] font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/70 ${value === o.value ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

const STATUS: Record<RequestStatus, [string, string]> = {
  draft: ['Draft', 'border-slate-500 text-slate-300'],
  open: ['Waiting for crew', 'border-amber-400/60 text-amber-300'],
  complete: ['All in', 'border-emerald-400/60 text-emerald-300'],
  closed: ['Closed', 'border-slate-600 text-slate-400'],
  cancelled: ['Cancelled', 'border-slate-600 text-slate-400'],
};
export function StatusChip({ status }: { status: RequestStatus }) {
  const [text, cls] = STATUS[status];
  return <span className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[13px] font-semibold ${cls}`}>{text}</span>;
}

/** Confirm dialog in the dashboard's colours: Escape and the backdrop cancel; focus starts on Cancel. */
export function SkipperConfirm({ title, children, confirmLabel, cancelLabel = 'Cancel', onConfirm, onCancel, busy, danger }: {
  title: string; children: ReactNode; confirmLabel: string; cancelLabel?: string; onConfirm: () => void; onCancel: () => void; busy?: boolean; danger?: boolean;
}) {
  const cancel = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    cancel.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);
  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 p-4" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div role="alertdialog" aria-modal="true" aria-labelledby="skipper-confirm-title" className="w-full max-w-lg space-y-4 rounded-2xl border border-slate-700 bg-slate-800 p-6">
        <h2 id="skipper-confirm-title" className="m-0 text-lg font-bold text-white">{title}</h2>
        <div className="text-base leading-relaxed text-slate-300">{children}</div>
        <div className="flex justify-end gap-2">
          <button ref={cancel} type="button" onClick={onCancel} className={S.ghost}>{cancelLabel}</button>
          <button type="button" onClick={onConfirm} disabled={busy} className={danger ? `${S.primary} !bg-red-500 !text-white` : S.primary}>{busy ? 'Working…' : confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

export function FormError({ children }: { children: ReactNode }) {
  if (!children) return null;
  return <div role="alert" className="rounded-xl border border-red-400/50 bg-red-500/10 px-4 py-3 text-base text-red-200">{children}</div>;
}
